<template>
  <Card class="recent-activity-widget">
    <template #default>
      <div class="recent-activity-widget__title row middle g-1">
        <div class="card__title">{{ $t('recentActivity') }}</div>

        <DropdownComponent placement="bottom-start">
          <ButtonComponent :label="triggerLabel" />

          <template #dropdown>
            <ButtonComponent
              v-for="preset in presets"
              :key="preset.value"
              :icon="preset.value === selectedPreset ? ['fal', 'check'] : null"
              @click="setFilter(preset.value)"
            >
              <template #label>
                <span class="row middle g-1">
                  <FontAwesomeIcon :icon="preset.icon" />
                  <span>{{ preset.label }}</span>
                </span>
              </template>
            </ButtonComponent>
          </template>
        </DropdownComponent>
      </div>

      <div class="recent-activity-widget__items">
        <component :is="activity.typename" v-for="(activity, index) in item.docs" :key="index" :value="activity" />
        <InfiniteLoading @infinite="next" :identifier="infiniteId">
          <template #spinner>
            <ListLoading />
          </template>
          <template #no-more>{{ $t('noMore', { x: $tc('activity', 2).toLowerCase() }) }}</template>
          <template #no-results>
            <EmptyState :title="$t('noActivity')" :icon="['fal', 'bolt']" class="grow-1 mb-2" />
          </template>
          <template #error="{ trigger }">
            <LoadError @retry="trigger" />
          </template>
        </InfiniteLoading>
      </div>
    </template>
  </Card>
</template>

<script>
import API from '@/api/LastActivity'
import Pagination from '@/mixins/Pagination'
import Card from '@/components/Card'
import EmptyState from '@/components/EmptyState'
import LoadError from '@/components/LoadError'
import ListLoading from '@/components/ListLoading'
import InfiniteLoading from 'vue-infinite-loading'
import { DropdownComponent } from 'vue-elder-dropdown'
import Types from './Types'

export default {
  mixins: [Pagination],
  props: {
    reference: {
      type: Object,
      required: true,
    },
  },
  watch: {
    'reference.ref': 'reset',
  },
  data() {
    return {
      selectedPreset: 'all',
      item: {
        docs: [],
        page: null,
        pages: null,
        total: 0,
      },
      paginate: {
        page: 1,
        limit: 20,
      },
    }
  },
  computed: {
    presets() {
      return [
        { value: 'all', label: this.$t('all'), icon: ['fal', 'bolt'] },
        { value: 'lead', label: this.$tc('lead', 2), icon: ['fal', 'user'], types: ['Lead'] },
        { value: 'comment', label: this.$tc('message', 2), icon: ['fal', 'comment'], types: ['Comment'] },
        { value: 'task', label: this.$tc('task', 2), icon: ['fal', 'tasks'], types: ['Task'] },
      ]
    },
    activePreset() {
      return this.presets.find(p => p.value === this.selectedPreset)
    },
    triggerLabel() {
      return `${this.activePreset.label} (${this.item.total})`
    },
  },
  methods: {
    fetch() {
      if (this.item.pages === 0 || (this.item.pages && this.item.page === this.item.pages)) return Promise.resolve()

      return API.get({
        reference: this.reference,
        paginate: this.paginate,
        filter: { types: this.activePreset.types },
      }).then(({ lastActivityPaginate }) => lastActivityPaginate)
    },
    setFilter(value) {
      this.selectedPreset = value
      this.item.total = 0
      this.reset()
    },
  },
  created() {
    this.$bus.$on('task:create', this.reset)
    this.$bus.$on('task:update', this.reset)
    this.$bus.$on('task:remove', this.reset)
  },
  beforeDestroy() {
    this.$bus.$off('task:create', this.reset)
    this.$bus.$off('task:update', this.reset)
    this.$bus.$off('task:remove', this.reset)
  },
  components: {
    Card,
    EmptyState,
    LoadError,
    ListLoading,
    InfiniteLoading,
    DropdownComponent,
    ...Types,
  },
}
</script>

<style lang="scss">
.recent-activity-widget {
  overflow: hidden;

  .card__content {
    display: flex;
    flex-direction: column;
    padding: 0;
    flex-grow: 1;
    overflow-y: auto;

    @include scrollShadow();
  }

  .card__title {
    margin-bottom: 0;
  }

  &__title {
    position: sticky;
    top: 0;
    z-index: 1;
    padding: var(--card-spacing);
    background: linear-gradient(to bottom, white 55%, rgba(255, 255, 255, 0));
  }

  &__title .elder-dropdown__trigger .elder-button {
    --vue-elder-button-padding-y: 0.25em;
    --vue-elder-button-padding-x: 0.6em;
    --vue-elder-default: hsl(var(--grey-h), var(--grey-s), 95%);
    --vue-elder-default-contrast: hsl(var(--grey-h), var(--grey-s), 20%);
    --vue-elder-default-border: transparent;

    font-size: var(--font-size-small);
  }

  &__items {
    padding: 0 var(--card-spacing) var(--card-spacing) var(--card-spacing);
    margin-top: calc(var(--card-spacing) * -0.5);
  }
}
</style>
